import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtOdds } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { TeamLogo } from "@/components/TeamLogo";
import { LeagueChip } from "@/components/LeagueLogo";
import { Sparkles, Calendar, MapPin, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";

const LEAGUES = ["All", "NBA", "NFL", "NHL", "MLB", "NCAAF", "NCAAB", "EPL", "MMA"];

export default function Games() {
  const [league, setLeague] = useState("All");
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = () => {
    setLoading(true);
    const params = league === "All" ? {} : { league };
    api.get("/games", { params })
      .then((r) => setGames(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [league]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/admin/refresh", null, { params: { scope: "top" } });
      toast.success(`Live data refreshed — ${data.total_games} games updated`, {
        description: data.quota ? `Odds API credits remaining: ${data.quota.requests_remaining}` : undefined,
      });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail ?? "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="games-page">
      <PageHeader
        title="Live odds board"
        subtitle="Moneylines, spreads, and totals from real bookmakers. Pulled live via The Odds API."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={refresh}
              disabled={refreshing}
              className="iso-btn-outline !py-1.5 !text-xs"
              data-testid="refresh-live-btn"
              title="Pull latest odds from The Odds API (top 4 leagues)"
            >
              {refreshing ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
              Refresh live
            </button>
            <div className="flex items-center gap-1 rounded-lg border border-border bg-gray-0 p-1 overflow-x-auto no-scrollbar">
              {LEAGUES.map((l) => (
                <button
                  key={l}
                  onClick={() => setLeague(l)}
                  data-testid={`league-${l.toLowerCase()}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-all whitespace-nowrap ${
                    league === l ? "bg-primary text-primary-foreground" : "text-gray-500 hover:text-gray-900"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
        }
      />

      {loading && <div className="iso-card p-8 text-center text-sm text-gray-500">Loading games...</div>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {games.map((g, i) => (
          <div
            key={g.id}
            className="iso-card p-5 transition-shadow hover:shadow-card-lg animate-fade-up"
            style={{ animationDelay: `${i * 50}ms` }}
            data-testid={`game-card-${g.id}`}
          >
            <div className="flex items-center justify-between">
              <LeagueChip code={g.league} />
              <div className="flex items-center gap-1 text-xs text-gray-500">
                <Calendar className="size-3" />
                {new Date(g.start_time).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
            </div>

            <div className="my-4 space-y-2">
              <TeamRow name={g.away_team_name} abbr={g.away_team_abbr} logo={g.away_logo} ml={g.odds.moneyline_away}
                spread={-g.odds.spread} spreadPrice={g.odds.spread_price_away} />
              <TeamRow name={g.home_team_name} abbr={g.home_team_abbr} logo={g.home_logo} ml={g.odds.moneyline_home}
                spread={g.odds.spread} spreadPrice={g.odds.spread_price_home} home />
            </div>

            <div className="flex items-center justify-between gap-2 border-t border-border pt-4">
              <div className="flex flex-1 items-center gap-2 text-xs text-gray-500">
                <MapPin className="size-3" /> {g.venue ?? "TBD"}
                <span className="mx-1 h-3 w-px bg-border" />
                Total <span className="font-semibold text-gray-900">{g.odds.total}</span>
              </div>
              <Link to={`/simulate?game=${g.id}`} className="iso-btn-primary !py-1.5 !text-xs" data-testid={`simulate-btn-${g.id}`}>
                <Sparkles className="size-3.5" /> Simulate
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const TeamRow = ({ name, abbr, logo, ml, spread, spreadPrice, home }) => (
  <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5">
    <div className="flex items-center gap-3">
      <TeamLogo src={logo} abbr={abbr} size={36} />
      <div>
        <div className="text-sm font-semibold text-gray-900">{name}</div>
        {home && <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400">Home</div>}
      </div>
    </div>
    <div className="flex items-center gap-2">
      <OddsBox label="Spread" value={`${spread > 0 ? "+" : ""}${spread}`} sub={fmtOdds(spreadPrice)} />
      <OddsBox label="ML" value={fmtOdds(ml)} highlight />
    </div>
  </div>
);

const OddsBox = ({ label, value, sub, highlight }) => (
  <div className={`min-w-[72px] rounded-md border px-2.5 py-1.5 text-center ${highlight ? "border-secondary/30 bg-secondary-lighter/40" : "border-border bg-gray-0"}`}>
    <div className="text-[9px] font-semibold uppercase tracking-wider text-gray-400">{label}</div>
    <div className="font-lexend text-sm font-bold text-gray-900">{value}</div>
    {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
  </div>
);
